import { alfred } from '@toggle/toggle';
import { v4 } from 'uuid';

import { config } from '~/config/app.config';
import { useActions } from '~/stores/use-actions/useActions';
import { hasActions } from '~/stores/use-actions/utils/hasActions';
import { apiFetch } from '~/utils/api-fetch/apiFetch';
import { apiStream } from '~/utils/api-stream/apiStream';
import { create } from '~/utils/store/createStore';

import { ChatStore, MessageReply, MessageStatus } from './use-chat.types';
import { sortReplies } from './utils/sortReplies';
import {
  appendReply,
  createNewMessage,
  updateMessage,
} from './utils/updateMessage';

export const useChat = create<ChatStore>((set, get) => ({
  sessionId: v4(),
  controller: new AbortController(),
  messages: [],
  isMessagePending: false,
  abortRequest: () => {
    get().controller.abort();
    set({ isMessagePending: false });
  },
  addFeedback: ({ sessionId, requestId, feedback }) => {
    apiFetch(alfred.feedback.path, {
      method: 'post',
      body: {
        session_id: sessionId,
        request_id: requestId,
        feedback,
      },
    });
    set(state => ({
      ...state,
      messages: state.messages.map(m => {
        return {
          ...m,
          reply: m.reply.map(reply =>
            'request_id' in reply && reply.request_id === requestId
              ? { ...reply, feedback }
              : reply
          ),
        };
      }),
    }));
  },
  resetSession: hideResetCompletedMessage => {
    const sessionId = v4();
    set(state => ({
      sessionId,
      messages: [
        ...state.messages,
        ...(hideResetCompletedMessage
          ? []
          : [createNewMessage({ status: 'complete', type: 'reset' })]),
      ],
    }));
  },
  append: message => {
    set(state => ({
      messages: [...state.messages, message],
    }));
  },
  setActionsFromReply: (primaryReply: MessageReply) => {
    if (hasActions(primaryReply?.data)) {
      useActions.getState().setActions(primaryReply.data.actions);
    }
  },
  removeReply: (messageId, source) => {
    set(({ messages }) => ({
      messages: messages.map(m => {
        if (m.id === messageId) {
          return { ...m, reply: m.reply.filter(r => r.source !== source) };
        }

        return m;
      }),
    }));
  },
  update: message => {
    set(state => ({
      messages: updateMessage({ messages: state.messages, message }),
    }));
  },
  sendMessage: async ({ question, target }) => {
    useActions.getState().removeAllActions();
    const id = v4();

    set(state => ({
      ...state,
      controller: new AbortController(),
      messages: [
        ...state.messages,
        createNewMessage({
          id,
          question,
        }),
      ],
      isMessagePending: true,
    }));

    const onComplete = (
      status: MessageStatus,
      hasExceededUsageLimit?: boolean
    ) => {
      const existingMessage = get().messages.find(m => m.id === id);

      if (existingMessage) {
        const sortedReplies = sortReplies(existingMessage.reply);

        get().update({
          ...existingMessage,
          reply: sortedReplies,
          status,
          hasExceededUsageLimit,
        });

        const [primaryReply] = sortedReplies;

        if (status === 'complete') {
          get().setActionsFromReply(primaryReply || {});
        }
      }

      set(() => ({
        isMessagePending: false,
      }));
    };

    const onReadChunk = (reply: MessageReply[]) => {
      set(({ messages }) => ({
        messages: appendReply(messages, { id, reply }),
      }));
    };

    apiStream<MessageReply>({
      body: {
        question,
        session_id: get().sessionId,
        target,
        mode: 'quick',
      },
      onReadChunk,
      onComplete: () => onComplete('complete'),
      onError: error => {
        const isUsageLimitExceeded = error?.status === 429;
        onComplete('error', isUsageLimitExceeded);
      },
      onAbort: () => onComplete('abort'),
      controller: get().controller,
      endpoint: `${config.api.root}${alfred.streaming.path}`,
    });
  },
  resetStore: () => {
    useActions.getState().removeAllActions();
    get().controller.abort();
    const controller = new AbortController();

    set(() => ({
      sessionId: v4(),
      controller,
      messages: [],
      isMessagePending: false,
    }));
  },

  shouldResetStateOnSubmit: false,
  setShouldResetStateOnSubmit: value => {
    set({ shouldResetStateOnSubmit: value });
  },
}));
